import React, {useState, useEffect, useMemo, useRef, useContext, createContext} from 'react';
import ErrorBoundary from './ErrorBoundary/ErrorBoundary';
import { classNames, getAppShellLayout } from '../../Services/helpers';
import { useHelp, scrollDirection } from '../Help';
import { useSession } from '../Session';
import usePersistPageScroll from './usePersistPageScroll';
import { WINDOWED_ATTRIBUTE } from './TileList';

const PageContext = createContext();

export const usePageContext = () => {
    return useContext(PageContext);
};

const Page = ({children, zeroPad, className, overflow, lightBg = false, persistScrollKey = null}) => {
    const { showRedesign, showDarkMode } = useSession();
    const {scrollHook} = useHelp();
    const scrollRef = useRef(null);
    const prevScrollTop = useRef(0);
    const lastScrollTime = useRef(Date.now());
    const [scrollTop, setScrollTop] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [offsetHeight, setOffsetHeight] = useState(window.innerHeight);
    const [offsetWidth, setOffsetWidth] = useState(window.innerWidth);
    const [clientHeight, setClientHeight] = useState(window.innerHeight);
    const [clientWidth, setClientWidth] = useState(window.innerWidth);


    const bars = useMemo(
        () => {
            const tabs = showRedesign ? 0 : (document.getElementById('tab-container')?.offsetHeight ?? 92);
            return tabs + (document.getElementById('top-bar')?.offsetHeight ?? 53);
        },
        [showRedesign]
    );

    useEffect(() => {
        if (!scrollRef?.current) {
            return;
        }

        const currentScrollRef = scrollRef.current;

        const listener = () => {
            const currentScrollTop = currentScrollRef.scrollTop;
            const currentTime = Date.now();
            const timeDiff = currentTime - lastScrollTime.current;
            const scrollDiff = currentScrollTop - prevScrollTop.current;

            // Calculate scroll velocity (pixels per millisecond)
            const velocity = Math.abs(scrollDiff / timeDiff);

            // Check if we're bouncing back from either extreme
            const isBouncingBack =
                (prevScrollTop.current < 0 && scrollDiff > 0) || // bouncing back from top
                (prevScrollTop.current > currentScrollRef.scrollHeight - currentScrollRef.clientHeight && scrollDiff < 0); // bouncing back from bottom

            // Only change direction if we're not bouncing back
            if (Math.abs(scrollDiff) > 2 && velocity < 0.3 && !isBouncingBack) {
                const direction = scrollDiff > 0 ? scrollDirection.DOWN : scrollDirection.UP;
                scrollHook.invoke(direction);
            }

            prevScrollTop.current = currentScrollTop;
            lastScrollTime.current = currentTime;
            setScrollTop(currentScrollTop);
            setScrollLeft(currentScrollRef.scrollLeft);
            setOffsetHeight(currentScrollRef.offsetHeight);
            setOffsetWidth(currentScrollRef.offsetWidth);
            setClientHeight(currentScrollRef.clientHeight);
            setClientWidth(currentScrollRef.clientWidth);
        }

        currentScrollRef.addEventListener('scroll', listener);

        // Add listener for TOP event
        const unsubscribe = scrollHook.listen((direction) => {
            if (direction !== scrollDirection.TOP) {
                return;
            }

            // A windowed TileList sets scrollTop as it measures rows, which cancels
            // an in-flight smooth scroll and strands the page part-way up.
            currentScrollRef.scrollTo({
                top: 0,
                behavior: currentScrollRef.querySelector(`[${WINDOWED_ATTRIBUTE}]`) ? 'auto' : 'smooth',
            });
        });

        return () => {
            currentScrollRef.removeEventListener('scroll', listener);
            unsubscribe();
        }
    }, [scrollHook]);

    const providerValues = {
        scrollTop,
        scrollLeft,
        offsetHeight,
        offsetWidth,
        clientHeight,
        clientWidth,
    }

    const appShellLayout = getAppShellLayout();

    return <PageContext.Provider value={providerValues}>
        {persistScrollKey ? <PageScrollPersistence {...{scrollRef, persistScrollKey}} /> : null}
        <div ref={scrollRef}
            id="the-page"
            className={
                classNames(
                    // 'no-scrollbar',
                    'relative flex flex-col w-full page-container z-20',
                    zeroPad ? 'pb-16' : 'page-padding pt-5 pb-16',
                    overflow ? overflow : 'overflow-auto overscroll-y-contain',
                    className ?? ((lightBg || showDarkMode) ? 'surface-2' : 'surface-1'),
                )
            }
            style={{
                maxHeight: appShellLayout !== 'mobile' ? `calc(100vh - 4rem)` : `calc(100vh - ${bars}px)`,
                minHeight: appShellLayout !== 'mobile' ? `calc(100vh - 4rem)` : `calc(100vh - ${bars}px)`,
            }}
        >
            <ErrorBoundary>
                {children}
            </ErrorBoundary>

            <p>&nbsp;</p>
            <p>&nbsp;</p>
        </div>
    </PageContext.Provider>
}

export default Page;

const PageScrollPersistence = ({scrollRef, persistScrollKey}) => {
    usePersistPageScroll(scrollRef, persistScrollKey);
    return null;
};
